import { Select } from "antd";
import { Visit, VisitMedicine } from "../../interfaces/Visit";
import React from "react";
import { changeVisitData, UpdateVisitField } from "../../features/visit/visitSlice";
import { useAppDispatch } from "../../hooks";

type MedicineInstructionsProp = {
  visit: Visit;
  isPreview?: boolean;
};

const LIQUID_FORMS = new Set(["syrup", "drops", "suspension", "solution", "injection", "linctus"]);

const FRACTION_LABELS: Record<string, string> = {
  "0.25": "¼", "0.5": "½", "0.75": "¾",
  "1.5": "1½", "2.5": "2½",
};

function fractionHint(val: any): string | null {
  return FRACTION_LABELS[String(Number(val))] ?? null;
}

const FORMS = [
  { label: "Tablet",     value: "tablet" },
  { label: "Capsule",    value: "capsule" },
  { label: "Syrup",      value: "syrup" },
  { label: "Drops",      value: "drops" },
  { label: "Suspension", value: "suspension" },
  { label: "Solution",   value: "solution" },
  { label: "Injection",  value: "injection" },
  { label: "Cream",      value: "cream" },
  { label: "Lotion",     value: "lotion" },
  { label: "Gel",        value: "gel" },
  { label: "Powder",     value: "powder" },
  { label: "Inhaler",    value: "inhaler" },
  { label: "Ointment",   value: "ointment" },
];

const MEAL_TYPES = [
  { name: "Before Meal",    value: "before_meal" },
  { name: "After Meal",     value: "after_meal" },
  { name: "With Meal",      value: "with_meal" },
  { name: "Empty Stomach",  value: "empty_stomach" },
];

const DURATIONS = [
  { name: "Days",   value: "days" },
  { name: "Weeks",  value: "weeks" },
  { name: "Months", value: "months" },
];

const FREQUENCIES = [
  { name: "Daily",          value: "daily" },
  { name: "Weekly",         value: "weekly" },
  { name: "Alternate Days", value: "alternate days" },
];

const SLOTS = [
  { key: "morning",   label: "M (Morning)" },
  { key: "afternoon", label: "A (Afternoon)" },
  { key: "evening",   label: "E (Evening)" },
  { key: "night",     label: "N (Night)" },
] as const;

const MedicineInstructions: React.FC<MedicineInstructionsProp> = ({ visit, isPreview }) => {
  const dispatch = useAppDispatch();

  const onChange = (value: any, key: string, medicine: VisitMedicine) => {
    const updated = visit.medicines.map((m) =>
      m.id === medicine.id ? { ...m, instructions: { ...m.instructions, [key]: value } } : { ...m }
    );
    updateField("medicines", updated);
  };

  const onRemove = (item: VisitMedicine) => {
    updateField("medicines", visit.medicines.filter((m) => m.id !== item.id));
  };

  const updateField = (fieldName: keyof Visit, value: any) => {
    const payload: UpdateVisitField = { fieldName, value };
    dispatch(changeVisitData(payload));
  };

  const medicineRow = (item: VisitMedicine, index: number) => {
    const isLiquid = LIQUID_FORMS.has((item.instructions.form || "").toLowerCase());

    return (
      <div key={item.id} className="block mb-3">
        {/* Header bar */}
        <div className="flex flex-row items-center w-full px-3 py-2 rounded-t-md bg-primary text-white">
          <span className="flex-1 font-semibold capitalize">
            {index + 1}. {item?.instructions?.name}
            {item?.instructions?.molecule && (
              <span className="ml-2 text-xs opacity-70 font-normal">{item.instructions.molecule}</span>
            )}
          </span>
          <button
            onClick={() => onRemove(item)}
            className="px-3 py-0.5 rounded-md text-sm font-medium bg-white/20 hover:bg-white/30 text-white"
          >
            Remove
          </button>
        </div>

        {/* Body */}
        <div className="p-3 bg-gray-50 border border-t-0 rounded-b-md">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {/* Form */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Form</label>
              <Select
                style={{ width: "100%" }}
                size="small"
                value={item?.instructions?.form}
                onChange={(v) => onChange(v, "form", item)}
                options={FORMS}
              />
            </div>

            {/* Duration */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Duration</label>
              <div className="flex gap-1">
                <input
                  type="number"
                  min={1}
                  value={item?.instructions?.duration || ""}
                  onChange={(e) => onChange(e.target.value, "duration", item)}
                  className="flex-1 min-w-0 py-1 px-2 text-sm border border-gray-300 rounded-md"
                  placeholder="e.g. 5"
                />
                <Select
                  style={{ width: 90 }}
                  size="small"
                  value={item?.instructions?.durationPeriod || "days"}
                  onChange={(v) => onChange(v, "durationPeriod", item)}
                  options={DURATIONS.map((d) => ({ label: d.name, value: d.value }))}
                />
              </div>
            </div>

            {/* Frequency */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Frequency</label>
              <Select
                style={{ width: "100%" }}
                size="small"
                value={item?.instructions?.frequency}
                onChange={(v) => onChange(v, "frequency", item)}
                options={FREQUENCIES.map((f) => ({ label: f.name, value: f.value }))}
              />
            </div>

            {/* Note */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Note</label>
              <input
                type="text"
                value={item?.instructions?.note || ""}
                onChange={(e) => onChange(e.target.value, "note", item)}
                className="w-full py-1 px-2 text-sm border border-gray-300 rounded-md"
                placeholder="e.g. if fever"
              />
            </div>
          </div>

          {/* Dosage row */}
          <div className="mt-3">
            <div className="flex items-baseline gap-2 mb-2">
              <label className="block text-xs font-medium text-gray-600">
                Dosage
                {isLiquid && <span className="ml-1 text-gray-400 font-normal">(ml per dose)</span>}
              </label>
              {!isLiquid && (
                <span className="text-xs text-gray-400">
                  0.5 → ½ &nbsp;·&nbsp; 0.25 → ¼ &nbsp;·&nbsp; 0.75 → ¾ &nbsp;·&nbsp; 1.5 → 1½ &nbsp;on prescription
                </span>
              )}
            </div>
            <div className="flex gap-3 flex-wrap">
              {SLOTS.map(({ key, label }) => {
                const val = item?.instructions?.[key];
                const hint = !isLiquid ? fractionHint(val) : null;
                return (
                  <div key={key} className="flex flex-col items-center gap-0.5">
                    <span className="text-xs text-gray-500">{label}</span>
                    <div className="relative">
                      <input
                        type="number"
                        min={0}
                        step={isLiquid ? 0.5 : 0.25}
                        value={val || ""}
                        onChange={(e) => onChange(parseFloat(e.target.value) || 0, key, item)}
                        className="w-16 py-1 px-2 text-sm text-center border border-gray-300 rounded-md"
                        placeholder="0"
                      />
                      {hint && (
                        <span className="absolute -bottom-4 left-0 right-0 text-center text-xs font-semibold text-indigo-600">
                          = {hint}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* spacer for hint labels */}
            <div className="h-5" />
          </div>

          {/* Meal timing */}
          <div className="mt-3">
            <label className="block text-xs font-medium text-gray-600 mb-2">Meal Timing</label>
            <div className="flex flex-wrap gap-2">
              {MEAL_TYPES.map((mt) => (
                <label
                  key={mt.value}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium cursor-pointer transition-colors ${
                    item?.instructions?.meal === mt.value
                      ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                      : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                  }`}
                >
                  <input
                    type="radio"
                    name={`meal-${item.id}`}
                    className="sr-only"
                    checked={item?.instructions?.meal === mt.value}
                    onChange={() => onChange(mt.value, "meal", item)}
                  />
                  {mt.name}
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="px-2 mt-2">
      {visit.medicines?.map((item, index) => medicineRow(item, index))}
    </div>
  );
};

export default MedicineInstructions;
